import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { Router, RouterLink } from '@angular/router';
import { LOCATION } from '@ng-web-apis/common';
import { I18NEXT_SERVICE, I18NextModule, ITranslationService } from 'angular-i18next';
import { from, mergeMap, Observable } from 'rxjs';
import { BaseComponent } from 'src/app/_shared/components/base/base.component';
import { IconComponent } from 'src/app/common/icon/icon.component';
import { FavouriteLanguage } from 'src/types/graphql';
import { LanguagePipe } from '../../dialogs/language-dialog/language/language.pipe';
import { OpenDialogService } from '../../../common/open-dialog/open-dialog.service';
import { RouteTranslateService } from '../../../common/route-translate/route-translate.service';
import { baseUrl, getDefaultModalSettings } from '../../../common/utils/helpers';
import { CookieService } from '../../../common/cookie/cookie.service';
import { RouteTranslatePipe } from '../../pipes/route-translate.pipe';

@Component({
  selector: 'sharry-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    AsyncPipe,
    RouterLink,
    I18NextModule,
    RouteTranslatePipe,
    IconComponent,
    MatExpansionModule,
    MatButtonModule,
    LanguagePipe,
  ],
})
export class FooterComponent extends BaseComponent {
  year = new Date().getFullYear();
  language$: Observable<FavouriteLanguage>;

  constructor(
    private openDialogService: OpenDialogService,
    private router: Router,
    private routeTranslateService: RouteTranslateService,
    private cookieService: CookieService,
    @Inject(I18NEXT_SERVICE) private translateService: ITranslationService,
    @Inject(LOCATION) private location: Location,
  ) {
    super();
    this.language$ = this.translateService.events.languageChanged.asObservable() as Observable<FavouriteLanguage>;
  }

  onLanguageChange() {
    this.subscriptions.push(
      from(import('../../dialogs/language-dialog/language-dialog.component'))
        .pipe(
          mergeMap(({ LanguageDialogComponent }) =>
            this.openDialogService.openDialog(LanguageDialogComponent, { ...getDefaultModalSettings() }).afterClosed(),
          ),
        )
        .subscribe({
          next: result => {
            if (result?.translateRoute) {
              const { translateRoute, queryParams, slug } = result;
              const navigationPart = [
                this.routeTranslateService.translateRoute(translateRoute?.[0].replace('routes.', '')),
              ];
              if (slug) {
                navigationPart.push(slug.replace('/', ''));
              }
              this.location.replace(
                baseUrl +
                  this.router
                    .createUrlTree(navigationPart, {
                      queryParams,
                    })
                    .toString(),
              );
            }
          },
        }),
    );
  }

  openSupportDialog(): void {
    import('../../dialogs/support-dialog/support-dialog.component').then(({ SupportDialogComponent }) =>
      this.openDialogService.openDialog(SupportDialogComponent, {
        ...{
          autoFocus: false,
          restoreFocus: false,
          width: '90vw',
          maxWidth: '450px',
          minHeight: '200px',
          maxHeight: '84vh',
        },
      }),
    );
  }

  onCookieClick() {
    this.subscriptions.push(this.cookieService.onManageCookies().subscribe());
  }
}
